import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Minus, Plus } from 'lucide-react';
import type { GuestSource, GuestStatus, Order, OrderItem, RestaurantTable } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useTables } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { useCurrency } from '../lib/currency';
import { OpenTableDialog } from './OpenTableDialog';
import { Badge, EmptyState, Modal, Spinner } from './ui';

/** How many units of a line are still free to move. */
const movableOf = (item: OrderItem) => item.quantity - item.paidQuantity;

/**
 * Move lines to another table — one dish, or a whole party's worth in a single
 * transfer. Guests move tables, or a party splits in two.
 *
 * Selection lives in here rather than on the order panel, whose tick boxes
 * already mean "send these to the kitchen". Opened from a line's Move button
 * that line starts ticked; opened from the panel's Move items button nothing
 * is, and the whole bill is there to choose from.
 *
 * Works the same for a line already fired: the kitchen ticket is untouched —
 * only which bill owns the item changes.
 */
export function MoveItemDialog({
  order,
  preselect,
  open,
  onClose,
  onMoved,
}: {
  order: Order;
  /** Opened from one line's Move button: start with just that line ticked. */
  preselect?: OrderItem | null;
  open: boolean;
  onClose: () => void;
  onMoved?: (destinationTableNumber: number) => void;
}) {
  const { money } = useCurrency();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: tables } = useTables();

  /** Quantity picked per line; absent or 0 means "not moving this one". */
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [opening, setOpening] = useState<RestaurantTable | null>(null);

  const movable = useMemo(
    () => order.items.filter((item) => item.status !== 'CANCELLED' && movableOf(item) > 0),
    [order.items],
  );

  useEffect(() => {
    if (!open) return;
    setPicked(preselect ? { [preselect.id]: movableOf(preselect) } : {});
    // Re-seed only when the dialog opens or the line it was opened for changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselect?.id]);

  const selection = Object.entries(picked)
    .filter(([, quantity]) => quantity > 0)
    .map(([orderItemId, quantity]) => ({ orderItemId, quantity }));

  const move = useMutation({
    mutationFn: (targetTableId: string) =>
      api.orders.moveItems(order.id, {
        targetTableId,
        items: selection.map(({ orderItemId, quantity }) => {
          const item = movable.find((candidate) => candidate.id === orderItemId);
          // Omitting the quantity moves the whole line, which keeps its ticket link.
          return quantity >= (item?.quantity ?? 0) ? { orderItemId } : { orderItemId, quantity };
        }),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(['order', order.id], result.sourceOrder);
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      void queryClient.invalidateQueries({ queryKey: ['order', result.destinationOrderId] });
      toast.success(
        result.movedItemCount === 1
          ? `Moved to Table ${result.destinationTableNumber}.`
          : `Moved ${result.movedItemCount} items to Table ${result.destinationTableNumber}.`,
      );
      onMoved?.(result.destinationTableNumber);
      onClose();
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not move those items.'),
  });

  const openTable = useMutation({
    mutationFn: (args: {
      tableId: string;
      guestCount: number;
      notes: string | null;
      guest: { guestSource: GuestSource | null; guestStatus: GuestStatus | null };
    }) => api.orders.open(args.tableId, args.guestCount, args.notes, args.guest),
    onSuccess: (newOrder) => {
      setOpening(null);
      move.mutate(newOrder.tableId);
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not open that table.'),
  });

  const busy = move.isPending || openTable.isPending;
  const otherTables = (tables ?? []).filter((table) => table.id !== order.tableId);
  const allPicked = movable.length > 0 && movable.every((item) => (picked[item.id] ?? 0) > 0);

  function set(item: OrderItem, quantity: number) {
    setPicked((current) => ({ ...current, [item.id]: Math.max(0, Math.min(movableOf(item), quantity)) }));
  }

  function pickTable(table: RestaurantTable) {
    if (busy || selection.length === 0) return;
    if (table.activeOrder) move.mutate(table.id);
    else setOpening(table);
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={selection.length > 1 ? 'Move items' : 'Move item'}
        subtitle={`From Table ${order.tableNumber} · Order #${order.orderNumber}`}
        size="lg"
      >
        {movable.length === 0 ? (
          <EmptyState
            title="Nothing to move"
            message="Every line here has either been voided or already paid for."
          />
        ) : (
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
                  What is moving?
                </span>
                <div className="rule-accent flex-1 opacity-40" />
                <button
                  type="button"
                  onClick={() =>
                    setPicked(
                      allPicked
                        ? {}
                        : Object.fromEntries(movable.map((item) => [item.id, movableOf(item)])),
                    )
                  }
                  className="text-[11px] font-medium text-ink-faint transition hover:text-accent"
                >
                  {allPicked ? 'Clear' : 'Select all'}
                </button>
              </div>

              <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {movable.map((item) => {
                  const quantity = picked[item.id] ?? 0;
                  const limit = movableOf(item);
                  const on = quantity > 0;
                  return (
                    <div
                      key={item.id}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition',
                        on ? 'border-accent/60 bg-accent/8' : 'border-line bg-canvas',
                      )}
                    >
                      <button
                        type="button"
                        aria-label={on ? `Do not move ${item.nameSnapshot}` : `Move ${item.nameSnapshot}`}
                        onClick={() => set(item, on ? 0 : limit)}
                        className={clsx(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition',
                          on ? 'border-accent bg-accent text-white' : 'border-line-strong',
                        )}
                      >
                        {on ? <Check size={13} strokeWidth={3} /> : null}
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-ink">
                          <span className="tnum text-accent">{item.quantity}×</span> {item.nameSnapshot}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-faint">
                          <span className="tnum">{money(item.lineSubtotalCents)}</span>
                          {item.status === 'DRAFT' ? <Badge tone="caution">Not sent</Badge> : null}
                          {item.paidQuantity > 0 ? (
                            <Badge tone="positive">{item.paidQuantity} paid — staying</Badge>
                          ) : null}
                        </div>
                      </div>

                      {/* Only a line with several free units needs a quantity choice. */}
                      {on && limit > 1 ? (
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            aria-label="Fewer"
                            onClick={() => set(item, quantity - 1)}
                            className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-line-strong hover:text-ink"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="tnum w-5 text-center text-sm text-ink">{quantity}</span>
                          <button
                            type="button"
                            aria-label="More"
                            onClick={() => set(item, quantity + 1)}
                            className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-line-strong hover:text-ink"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint">
                  Move to table
                </span>
                <span className="text-[11px] text-ink-faint">
                  {selection.length === 0
                    ? 'Pick at least one item first'
                    : `${selection.length} item${selection.length === 1 ? '' : 's'} selected`}
                </span>
              </div>

              {otherTables.length === 0 ? (
                <EmptyState title="No other tables" message="There is nowhere else to move these to." />
              ) : (
                <div
                  className={clsx(
                    'grid grid-cols-3 gap-2 sm:grid-cols-5',
                    selection.length === 0 && 'pointer-events-none opacity-40',
                  )}
                >
                  {otherTables.map((table) => (
                    <button
                      key={table.id}
                      type="button"
                      disabled={busy || selection.length === 0}
                      onClick={() => pickTable(table)}
                      className={clsx(
                        'flex flex-col items-center gap-1 rounded-lg border px-2 py-3 text-center transition disabled:cursor-not-allowed',
                        table.activeOrder
                          ? 'border-line hover:border-accent/50'
                          : 'border-dashed border-line text-ink-faint hover:border-line-strong',
                      )}
                    >
                      <span className="font-display text-xl text-ink">
                        {String(table.number).padStart(2, '0')}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-ink-faint">
                        {table.activeOrder ? 'Open bill' : 'Opens new bill'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {busy ? <Spinner label="Moving…" /> : null}
          </div>
        )}
      </Modal>

      <OpenTableDialog
        table={opening}
        open={opening !== null}
        busy={openTable.isPending}
        onCancel={() => setOpening(null)}
        onConfirm={(guestCount, notes, guest) =>
          opening && openTable.mutate({ tableId: opening.id, guestCount, notes, guest })
        }
      />
    </>
  );
}
